/* eslint-disable @typescript-eslint/no-explicit-any -- Jotai atoms handle dynamic Firestore/Supabase data with varying shapes */
import { atom } from 'jotai'
import type { Atom } from 'jotai'
import { getLocalStoreDate } from '../utils/timezoneUtils'
import { getPlanInfo } from '../utils/pricingPlans'
import {
  compareProductCategories,
  getProductsByBarcodeDict,
  getProductsByCategoryDict,
  getProductsByCategoryList,
  getProductsListForSearch,
} from '../utils/productListHelpers'
import { differenceInMinutes } from 'date-fns'
import Fuse from 'fuse.js'
import { PRODUCT_FUSE_OPTIONS } from '../utils/fuseHelper'
import * as Localization from 'expo-localization'
import i18n from 'i18next'
import { NativeDateService } from '@ui-kitten/components'
import { z } from 'zod'

import { atomWithAsyncStorage } from './atomWithAsyncStorage'
import type {
  AtomUser,
  AtomStore,
  AtomProductCategory,
  AtomRole,
  StoresDict,
  ProductsDict,
  ProductCategoriesDict,
  SuppliersDict,
  RolesDict,
  UsersDict,
  DailyStatsDict,
  DailyStat,
  Constants,
  SaleSettings,
  ProductsUpdated,
  NewSaleInterfaceSettings,
  UserLocale,
  ThemePreference,
  ProductUnitsDict,
  DiscountOption,
  ProductModifier,
  Wallet,
  PaymentMethod,
  PriceType,
} from './AtomsProvider.types'
import {
  UserLocaleSchema,
  ThemePreferenceSchema,
  NewSaleInterfaceSettingsSchema,
  PrintTemplatesSchema,
} from './AtomsProvider.types'
import type { Organization, PrintTemplate, InvoiceSettings } from '../types'
import { printTemplatesApi } from './API/typedApiClient'
import { localDailyStatsOverlayAtom, localProductsOverlayAtom } from './Write/localViewState'
import { applyDailyStatsOverlay, applyProductsOverlay } from './Write/localViewOverlay'

// ============================================
// User Atoms
// ============================================

export const userAtom = atom<AtomUser | undefined>(undefined)
export const userIdAtom: Atom<string | undefined> = atom((get) => get(userAtom)?.id)
export const userStoreIdAtom: Atom<string | undefined> = atom((get) => get(userAtom)?.store?.id)

// Flag to prevent data loading during signup (when Firebase user exists but Supabase user doesn't yet)
export const signupInProgressAtom = atom<boolean>(false)

export const userLocaleAtom = atomWithAsyncStorage<UserLocale>(
  'userLocale',
  Localization.getLocales().shift()?.languageCode ?? 'en',
  { schema: UserLocaleSchema }
)

export const localeDateServiceAtom: Atom<NativeDateService> = atom((get) => {
  const userLocale = get(userLocaleAtom)
  let localeDateService: NativeDateService
  if (userLocale === 'ru') {
    const dateServiceI18n = i18n.t('dateService', { returnObjects: true })
    // Guard against i18n not being ready (returns string key instead of object)
    // Must have both dayNames and monthNames to be valid i18n config
    if (
      dateServiceI18n &&
      typeof dateServiceI18n === 'object' &&
      'dayNames' in dateServiceI18n &&
      'monthNames' in dateServiceI18n
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Runtime-validated i18n object
      localeDateService = new NativeDateService('ru', {
        i18n: dateServiceI18n as any,
        startDayOfWeek: 1,
      })
    } else {
      // Fallback to English if Russian translations aren't loaded
      localeDateService = new NativeDateService('en')
    }
  } else {
    localeDateService = new NativeDateService('en')
  }
  return localeDateService
})

// ============================================
// Database Atom
// ============================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const dbAtom = atom<any>(undefined)

// ============================================
// Device-level Backend Setting
// ============================================

/** Storage key for device-level Supabase setting - shared between atom and cache in types.ts */
export const SUPABASE_DEVICE_SETTING_KEY = 'useSupabaseDeviceSetting'

/** Default value when no setting is stored */
export const DEFAULT_SUPABASE_SETTING = false

/**
 * Device-level setting to control whether to use Supabase or Firestore.
 * This allows different devices in the same organization to use different backends for testing.
 * Default is false (Firestore enabled).
 */
export const useSupabaseDeviceSettingAtom = atomWithAsyncStorage<boolean>(
  SUPABASE_DEVICE_SETTING_KEY,
  DEFAULT_SUPABASE_SETTING,
  { schema: z.boolean() }
)

// ============================================
// Theme Atoms
// ============================================

/**
 * User's theme preference persisted to AsyncStorage.
 * Can be 'light', 'dark', or 'system' (follow device setting).
 */
export const themePreferenceAtom = atomWithAsyncStorage<ThemePreference>(
  'themePreference',
  'system',
  { schema: ThemePreferenceSchema }
)

/**
 * Holds the current system color scheme as reported by the device.
 * Updated from a React component that listens to `Appearance` changes.
 */
export const systemColorSchemeAtom = atom<'light' | 'dark'>('light')

/**
 * Resolves the user's theme preference to an actual theme value.
 * When preference is 'system', returns the current device color scheme.
 */
export const resolvedThemeAtom: Atom<'light' | 'dark'> = atom((get) => {
  const preference = get(themePreferenceAtom)
  if (preference === 'system') {
    return get(systemColorSchemeAtom)
  }
  return preference
})

// ============================================
// Daily Stats Atoms
// ============================================

/**
 * ATL-1445 stage 3 P6 — the daily stats AS THE SERVER LAST SAID: the Firestore listener's documents or
 * the Supabase fetch (`AppStack.subscribeToDailyStats`). This is the atom every writer sets.
 */
export const serverDailyStatsAtom = atom<DailyStatsDict>({})

/**
 * What the device shows: the server's daily stats plus this device's own unsent writes for the day
 * each entry is (the local view, `global/Write/localView.ts`; plan §3). READ-ONLY — write
 * `serverDailyStatsAtom` (`localViewWriters.test.js` fails on any writer of this one). With no
 * pending writes (every session today) it IS `serverDailyStatsAtom`'s object, the same reference.
 */
export const dailyStatsAtom: Atom<DailyStatsDict> = atom((get) =>
  applyDailyStatsOverlay(get(serverDailyStatsAtom), get(localDailyStatsOverlayAtom))
)

export const dailyStatsSalesAtom: Atom<Record<string, { saleAmount?: number; saleQuantity?: number }>> =
  atom((get) => {
    const ds = get(dailyStatsAtom)
    const newDS: Record<string, { saleAmount?: number; saleQuantity?: number }> = {}
    for (const key in ds) {
      newDS[key] = { saleAmount: ds[key].saleAmount, saleQuantity: ds[key].saleQuantity }
    }
    return newDS
  })

export const dailyStatsShiftStatusAtom: Atom<Record<string, { shiftOpened?: boolean; shiftClosed?: boolean }>> =
  atom((get) => {
    const ds = get(dailyStatsAtom)
    const newDS: Record<string, { shiftOpened?: boolean; shiftClosed?: boolean }> = {}
    for (const key in ds) {
      newDS[key] = { shiftOpened: ds[key].shiftOpened, shiftClosed: ds[key].shiftClosed }
    }
    return newDS
  })

export const userStoreDailyStatAtom: Atom<DailyStat | undefined> = atom((get) => {
  const storeId = get(userStoreIdAtom)
  return storeId ? get(dailyStatsAtom)[storeId] : undefined
})

// ============================================
// Store Atoms
// ============================================

export const storesDictAtom = atom<StoresDict>({})
export const storesListAtom: Atom<AtomStore[]> = atom((get) => Object.values(get(storesDictAtom)))
export const userStoreAtom: Atom<AtomStore | undefined> = atom((get) => {
  const storeId = get(userStoreIdAtom)
  return storeId ? get(storesDictAtom)[storeId] : undefined
})

export const visibleStoresForUserAtom: Atom<StoresDict> = atom((get) => {
  const storesDict = get(storesDictAtom)
  const userRole = get(userRoleAtom)
  const userStoreId = get(userStoreIdAtom)
  const visibleStores: StoresDict = {}
  for (const storeId in storesDict) {
    // Check firestore_id for 'admin' role (migrated data) or id for legacy
    const isAdmin = userRole?.firestore_id === 'admin' || userRole?.id === 'admin'
    if (storeId === userStoreId || userRole?.permissions?.['viewOtherStores'] || isAdmin) {
      visibleStores[storeId] = storesDict[storeId]
    }
  }
  return visibleStores
})

export const userInDifferentTimeZoneAtom: Atom<boolean> = atom((get) => {
  const userStore = get(userStoreAtom)
  if (userStore?.timezone) {
    const userToday = new Date()
    const storeToday = getLocalStoreDate(userStore?.id, userToday)
    if (userToday.getHours() !== storeToday.getHours()) {
      return true
    }
  }
  return false
})

// ============================================
// Organization Atoms
// ============================================

export const organizationAtom = atom<Organization | Record<string, unknown>>({})

export const organizationSubscriptionInfoAtom = atom((get) => {
  const organization = get(organizationAtom) as Organization
  const planInfo = getPlanInfo(
    organization?.subscriptionStatus === 'deleted' ? null : organization?.subscriptionPlan
  )
  // ATL-615: liftBlockTill arrives as a Firestore Timestamp (Firestore read mode) OR an ISO string
  // (Supabase read mode). Coerce to a Date so an expired override doesn't silently keep the block
  // lifted after the Phase-5 read cutover. Returns null when absent/unparseable.
  const liftBlockTillRaw = (organization as any)?.liftBlockTill
  const liftBlockTillDate: Date | null = (() => {
    if (liftBlockTillRaw == null) return null
    if (typeof liftBlockTillRaw?.toDate === 'function') return liftBlockTillRaw.toDate()
    const d = new Date(liftBlockTillRaw)
    return isNaN(d.getTime()) ? null : d
  })()
  return {
    id: organization?.id,
    ref: organization?.ref,
    name: organization?.name,
    pricingModel: organization?.pricingModel,
    subscriptionPlanInfo: planInfo,
    subscriptionStatus: organization?.subscriptionStatus,
    subscriptionPlan: organization?.subscriptionPlan,
    subscriptionSource: organization?.subscriptionSource,
    subscriptionLastUpdated: organization?.subscriptionLastUpdated,
    currentMonthSalesUSD: organization?.currentMonthSalesUSD,
    failedPayment: organization?.subscriptionStatus === 'past_due',
    // ATL-1923: the paid term ENDED because a charge failed — not a cancellation, and not a sales
    // overrun. An App Store card failure without a grace period delivers BILLING_ISSUE and
    // EXPIRATION in the same second, so `past_due` above is never observed there: the org goes
    // straight to `deleted`, the plan resolves to free, and `isBlocked` below fires on the free
    // limit. `lastSubscriptionPaymentFailed` (set by RevenueCat BILLING_ISSUE / Paddle
    // subscription_payment_failed; cleared by RENEWAL, INITIAL_PURCHASE, UNCANCELLATION or
    // subscription_payment_succeeded) is what still says why. It changes the wording only, never
    // who is blocked.
    paymentLapsed:
      organization?.subscriptionStatus === 'deleted' && organization?.lastSubscriptionPaymentFailed === true,
    overBillingLimits: !!(
      planInfo?.limits?.sales?.usd &&
      organization?.currentMonthSalesUSD &&
      organization.currentMonthSalesUSD > planInfo.limits.sales.usd
    ),
    isBlocked: !!(
      planInfo?.limits?.sales?.usd &&
      organization?.currentMonthSalesUSD &&
      organization.currentMonthSalesUSD > planInfo.limits.sales.usd * 1.2 &&
      (liftBlockTillDate == null || liftBlockTillDate < new Date())
    ),
    onFreePlan: organization?.subscriptionPlan === 'free',
  }
})

export const organizationIdAtom = atom((get) => {
  const organization = get(organizationAtom)
  return organization?.id
})

export const onboardingTasksLeftAtom = atom((get) => {
  const organization = get(organizationAtom) as any
  return organization?.onboardingTasksLeft
})

export const shouldShowOnboardingPopoverAtom: Atom<boolean> = atom((get) => {
  const organization = get(organizationAtom) as any
  if (organization?.pricingModel === 'trial_19_08_23' && organization?.subscriptionStatus !== 'active') {
    return false
  }
  const today = new Date()
  return differenceInMinutes(today, new Date(organization?.createdDate?.toDate?.())) < 5
})

export const currencyAtom = atom((get) => (get(organizationAtom) as Organization)?.currency)

export const currencyFormatterAtom = atom((get) => {
  const orgCurrency = (get(organizationAtom) as Organization)?.currency
  // Get currency code from the 'id' field
  const currencyCode = orgCurrency?.id
  if (!orgCurrency || !currencyCode) {
    // Return a default formatter if no currency or currency code is available
    return {
      custom: true,
      format: function (number: number | undefined | null) {
        return (number ?? 0).toString()
      },
    }
  }
  if (!orgCurrency.shortText) orgCurrency.shortText = orgCurrency.text
  const numberFormat = new Intl.NumberFormat(orgCurrency?.locale ?? 'ru-RU', {
    minimumFractionDigits: orgCurrency?.currencyDecimalPlaces ?? 0,
    maximumFractionDigits: orgCurrency?.currencyDecimalPlaces ?? 0,
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'narrowSymbol',
  })
  const customNumberFormat = {
    custom: true,
    format: function (number: number | undefined | null) {
      let output = numberFormat.format(number ?? 0)
      output = output.replace(currencyCode?.toUpperCase(), orgCurrency?.shortText ?? '')
      return output
    },
  }
  return customNumberFormat
})

// ============================================
// Role Atoms
// ============================================

export const rolesDictAtom = atom<RolesDict>({})
export const userRoleAtom: Atom<AtomRole | undefined> = atom((get) => {
  const user = get(userAtom)
  return user?.role ? get(rolesDictAtom)[user.role] : undefined
})

// Helper to check if user has admin role (handles both migrated and legacy data)
export const isAdminAtom: Atom<boolean> = atom((get) => {
  const userRole = get(userRoleAtom)
  return userRole?.firestore_id === 'admin' || userRole?.id === 'admin'
})

// ============================================
// Product Atoms
// ============================================

export const productUnitsDictAtom = atom<ProductUnitsDict>({})
/**
 * ATL-1445 stage 3 P6 — the product catalogue AS THE SERVER LAST SAID: the Firestore listener, the
 * Supabase initial load and realtime refetches, and the two `products.ts` writers' immediate merge.
 * This is the atom every writer sets (and reads, to merge into).
 */
export const serverProductsDictAtom = atom<ProductsDict>({})

/**
 * What the device shows: the server's catalogue plus this device's own unsent writes — stock sold
 * offline, products created offline (plan §3, §9 risk 5). READ-ONLY — write `serverProductsDictAtom`
 * (`localViewWriters.test.js` fails on any writer of this one). With no pending writes (every session
 * today) it IS `serverProductsDictAtom`'s object, the same reference, so nothing memoised on it
 * re-renders. `findInsufficientStockLine` reads this atom unchanged, and so counts the device's own
 * unsent sales (invariants 8 and 10).
 */
export const productsDictAtom: Atom<ProductsDict> = atom((get) =>
  applyProductsOverlay(get(serverProductsDictAtom), get(localProductsOverlayAtom))
)
export const productsUpdatedAtom = atom<ProductsUpdated>({
  updateCount: 0,
  lastServerUpdate: null,
  lastLocalUpdate: null,
})

export const productsByBarcodeDictAtom = atom((get) =>
  getProductsByBarcodeDict(get(productsDictAtom))
)

export const activeProductsByCategoryListAtom = atom((get) => {
  const productsDict = get(productsDictAtom)
  const productCategoriesDict = get(productCategoriesDictAtom)
  const userStoreId = get(userStoreIdAtom)
  return getProductsByCategoryList(productsDict, productCategoriesDict, true, userStoreId as any)
})

export const allProductsByCategoryListAtom = atom((get) => {
  const productsDict = get(productsDictAtom)
  const productCategoriesDict = get(productCategoriesDictAtom)
  return getProductsByCategoryList(productsDict, productCategoriesDict, false, null)
})

export const allProductsByCategoryDictAtom = atom((get) => {
  const productsDict = get(productsDictAtom)
  const productCategoriesDict = get(productCategoriesDictAtom)
  return getProductsByCategoryDict(productsDict, productCategoriesDict, false, null)
})

export const productCategoriesDictAtom = atom<ProductCategoriesDict>({})
// ATL-1892 — in the tabs' total order, not the dict's insertion order: the insertion order is
// whatever the transport delivered (Firestore's orderBy('order'), or the backend's read order), and the
// category list screen derives each category's new `order` from its index here.
export const productCategoriesListAtom: Atom<AtomProductCategory[]> = atom((get) =>
  Object.values(get(productCategoriesDictAtom)).sort(compareProductCategories)
)

export const productModifiersListAtom: Atom<ProductModifier[]> = atom((get) => {
  const constants = get(constantsAtom)
  return constants?.productModifiers?.data || []
})

// ONE shared Fuse index over every product (enriched once). Search results are
// scoped to a store at query time via searchProductsByStore() in fuseHelper, instead
// of holding a separate index + per-product clone per store — the top heap driver in
// the iOS background-OOM watchdog terminations (ATL-631).
export const productFuseSearchAtom = atom((get) => {
  const productsDict = get(productsDictAtom)
  const suppliersDict = get(suppliersDictAtom)
  const productCategoriesDict = get(productCategoriesDictAtom)
  const productsList = getProductsListForSearch(productsDict, suppliersDict, productCategoriesDict)
  return new Fuse(productsList, PRODUCT_FUSE_OPTIONS)
})

// ============================================
// Supplier Atoms
// ============================================

export const suppliersDictAtom = atom<SuppliersDict>({})

// ============================================
// Discount & User Atoms
// ============================================

export const discountOptionsListAtom = atom<DiscountOption[]>([])
export const usersDictAtom = atom<UsersDict>({})
export const inactiveUsersAtom = atom<UsersDict>({})
export const usersListAtom: Atom<AtomUser[]> = atom((get) => Object.values(get(usersDictAtom)))

// ============================================
// Constants Atoms
// ============================================

export const constantsAtom = atom<Constants>({})

export const walletsDictAtom: Atom<Record<string, Wallet>> = atom((get) => {
  const wallets: Record<string, Wallet> = {}
  get(constantsAtom)?.wallets?.data?.forEach((element) => {
    wallets[element.id] = element
  })
  return wallets
})

export const paymentMethodsDictAtom: Atom<Record<string, PaymentMethod>> = atom((get) => {
  const pms: Record<string, PaymentMethod> = {}
  get(constantsAtom)?.paymentMethods?.data?.forEach((element) => {
    pms[element.id] = element
  })
  return pms
})

export const priceTypesAtom: Atom<PriceType[]> = atom((get) => {
  const constants = get(constantsAtom)
  return constants?.priceTypes?.data || []
})

// ============================================
// Sale Interface Settings Atoms
// ============================================

export const newSaleInterfaceSettingsAtom = atomWithAsyncStorage<NewSaleInterfaceSettings>(
  'newSaleInterfaceSettings',
  { gridLeftWidth: 5, allowMultipleProductSelection: true },
  { schema: NewSaleInterfaceSettingsSchema as any }
)

// Sale settings atom - store-specific settings from database
export const saleSettingsAtom: Atom<Record<string, SaleSettings>> = atom((get) => {
  const constants = get(constantsAtom)
  return constants?.saleSettings?.data || {}
})

// Current store's sale settings atom
export const currentStoreSaleSettingsAtom: Atom<SaleSettings> = atom((get) => {
  const saleSettings = get(saleSettingsAtom)
  const userStoreId = get(userStoreIdAtom)
  return (
    (userStoreId ? saleSettings[userStoreId] : undefined) || {
      deliveryPrice: 0,
      deliveryPaidByCustomer: true,
      deliveryIncludeInReceipt: true,
      deliveryPaymentSourceWallet: null,
      defaultDeliverySpendingCategory: null,
      showSaveCartForLater: true,
      allowMultipleProductSelection: true,
      dateFieldVisible: false,
      storeFieldVisible: false,
      tableNumberFieldVisible: false,
      commentFieldVisible: false,
      showTotalPurchasePriceInCart: false,
      showPurchasePriceInEditCartItem: false,
      mergeSimilarItems: true,
    }
  )
})

// ============================================
// Print Templates Atoms
// ============================================

export const printTemplatesAtom = atomWithAsyncStorage<PrintTemplate[]>('printTemplates', [], {
  schema: PrintTemplatesSchema as any,
})

type UnsubscribeFn = (() => void) | null
export const printTemplatesUnsubscribeAtom = atom<UnsubscribeFn>(null)

// Polling interval for print templates (5 minutes)
const PRINT_TEMPLATES_POLL_INTERVAL = 5 * 60 * 1000

// Fetch print templates from Supabase API
export const setupPrintTemplatesListenerAtom = atom(
  null,
  async (
    get,
    set,
    { storeId, db: _db, templateType = 'sale' }: { storeId: string; db: any; templateType?: string }
  ) => {
    if (!storeId) {
      console.warn('storeId is required for template fetching')
      return
    }

    // Clean up existing polling interval if any
    const existingUnsubscribe = get(printTemplatesUnsubscribeAtom)
    if (existingUnsubscribe && typeof existingUnsubscribe === 'function') {
      console.log('Cleaning up existing template polling interval')
      existingUnsubscribe()
    }

    // Function to fetch templates from Supabase API
    const fetchTemplates = async () => {
      try {
        const apiTemplates = await printTemplatesApi.getPrintTemplatesByTypeAndStore({
          type: templateType,
          storeId,
        })

        // Transform API response to match PrintTemplate type
        const templates: PrintTemplate[] = apiTemplates.map((t) => ({
          id: t.id,
          firestoreId: t.firestoreId ?? undefined,
          name: t.name,
          type: (t.type ?? 'sale') as 'sale' | 'supply' | 'supplyOrder',
          content: t.content ?? undefined,
          stores: t.stores ?? undefined,
          storeId: t.stores?.[0] ?? undefined,
          isDefault: t.isDefault ?? undefined,
          organizationId: t.organizationId ?? undefined,
          createdAt: t.createdAt ? new Date(t.createdAt) : new Date(),
          updatedAt: t.updatedAt ? new Date(t.updatedAt) : new Date(),
          createdBy: t.createdBy ?? '',
          updatedBy: t.updatedBy ?? '',
          settings: {} as InvoiceSettings,
        }))

        // Update the main print templates atom
        const currentTemplates = get(printTemplatesAtom)
        const updatedTemplates = currentTemplates.filter(
          (template) => template.type !== templateType
        )
        const newTemplates = [...updatedTemplates, ...templates]
        set(printTemplatesAtom, newTemplates)

        console.log(`${templateType} print templates updated via Supabase API:`, templates.length)
      } catch (error) {
        console.error(`Error fetching ${templateType} print templates:`, error)
      }
    }

    // Fetch templates immediately
    await fetchTemplates()

    // Set up polling interval for updates
    const intervalId = setInterval(fetchTemplates, PRINT_TEMPLATES_POLL_INTERVAL)

    // Create unsubscribe function
    const unsubscribe = () => {
      clearInterval(intervalId)
    }

    // Store the unsubscribe function for cleanup
    set(printTemplatesUnsubscribeAtom as any, unsubscribe)

    return unsubscribe
  }
)

// Sale print templates atom - only filters templates from main atom
export const salePrintTemplatesAtom: Atom<PrintTemplate[]> = atom((get) => {
  const allTemplates = get(printTemplatesAtom)
  return allTemplates.filter((template) => template.type === 'sale')
})

// Template management atoms (if needed for complex logic)
export const templatesByTypeAtom = atom((get) => (type: string) => {
  const allTemplates = get(printTemplatesAtom)
  return allTemplates.filter((template) => template.type === type)
})

// ============================================
// Re-export atomWithAsyncStorage for backward compatibility
// ============================================
export { atomWithAsyncStorage }
